import { useQueryClient } from "@tanstack/react-query";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useSyncExternalStore } from "react";

import { AppShell } from "@/app/AppShell";
import { LoginScreen } from "@/features/auth/LoginScreen";
import { clearTenantLocalCache } from "@/lib/offline/db";
import { clearSession, getSession, onSessionChange } from "@/lib/session";
import { applyTenantTheme, getEffectiveTenantSettings } from "@/lib/theme";

/**
 * Puerta de entrada.
 *
 * Determina el acceso según la sesión y la ruta solicitada:
 * - "/" (Landing comercial): Pública, accesible sin sesión. Si el usuario ya está autenticado,
 *   se le redirige a su área de trabajo ("/pos").
 * - "/login" (Inicio de sesión): Público. Si ya está autenticado, se redirige a "/pos".
 * - Rutas protegidas ("/pos", "/inventario", "/caja", etc.): Requieren sesión activa.
 *   Si no hay sesión, se muestra el formulario de login. Si hay sesión, se monta el `AppShell`.
 */
export function RootGate() {
  const session = useSyncExternalStore(onSessionChange, getSession, () => null);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lastTenantIdRef = useRef<string | null>(session?.tenant?.id ?? null);

  // Cuando cambia el negocio activo (login con otra cuenta o suplantación),
  // limpiamos las cachés en memoria y en IndexedDB y aplicamos su tema.
  useEffect(() => {
    const currentTenantId = session?.tenant?.id ?? null;
    if (lastTenantIdRef.current && lastTenantIdRef.current !== currentTenantId) {
      queryClient.clear();
      void clearTenantLocalCache();
    }
    lastTenantIdRef.current = currentTenantId;

    if (session?.tenant) {
      applyTenantTheme(getEffectiveTenantSettings(session.tenant), session.tenant.slug);
    } else {
      applyTenantTheme(null);
    }
  }, [session?.tenant?.id, session?.tenant?.slug, session?.tenant?.settings, queryClient]);

  // Si el usuario ya tiene sesión activa y navega a la landing o a /login,
  // lo redirigimos automáticamente a su terminal de trabajo (/pos).
  useEffect(() => {
    if (session && (pathname === "/" || pathname === "/login")) {
      void navigate({ to: "/pos", replace: true });
    }
  }, [session, pathname, navigate]);

  // Pantallas públicas accesibles sin sesión
  if (!session) {
    if (pathname === "/" || pathname === "/login" || pathname === "/contacto") {
      return <Outlet />;
    }
    // Para cualquier otra ruta protegida intentada sin sesión
    return (
      <LoginScreen
        onSuccess={() => {
          void navigate({ to: "/pos", replace: true });
        }}
      />
    );
  }

  // Página pública de contacto también accesible con sesión
  if (pathname === "/contacto") {
    return <Outlet />;
  }

  // Si ya tiene sesión pero la ruta es "/" o "/login", evitamos parpadeo mientras el useEffect redirige
  if (pathname === "/" || pathname === "/login") {
    return null;
  }

  // Rutas protegidas con sesión activa dentro del AppShell
  return (
    <AppShell
      onLogout={() => {
        queryClient.clear();
        void clearTenantLocalCache();
        clearSession();
        void navigate({ to: "/login", replace: true });
      }}
    />
  );
}
